import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import '@vidstack/react/player/styles/default/theme.css';
import '@vidstack/react/player/styles/default/layouts/video.css';
import {
  MediaPlayer,
  MediaPlayerInstance,
  MediaProvider,
  Track,
  isHLSProvider,
} from '@vidstack/react';
import {
  defaultLayoutIcons,
  DefaultVideoLayout,
} from '@vidstack/react/player/layouts/default';
import { Button } from '../Button';
import { settleHls } from '../../shared/settleHls';
import {
  decoderRecovery,
  isDecodeFailure,
} from '../../shared/decoderRecovery';
import './PreviewPlayer.scss';

/**
 * The library's player, for the screens that show a file before it is
 * a finished title: the wizard's cover and subtitle steps, and New
 * Files' review.
 *
 * Those used to have a player of their own, with an hls.js from years
 * before the library's and none of what the library's had learnt -
 * aiming high on Auto, starting from the device's last estimate, the
 * phone's limits, bringing the picture back when the decoder gives up.
 * And no quality menu, so the original could not be asked for by hand,
 * which is the one thing a review from a thin connection wants. This
 * is the same player with the same setup (shared/settleHls), less what
 * only a finished title has: a resume point, progress, chapters and
 * preview thumbnails.
 *
 * Plays whatever it is given. The backend hands out the ladder or the
 * plain file depending on HLS_PLAYBACK, and both play here.
 */

export interface PreviewTrack {
  src: string;
  kind: 'subtitles' | 'captions';
  label: string;
  lang: string;
  default?: boolean;
}

export interface PreviewPlayerHandle {
  /** Where playback is, in seconds. */
  currentTime: () => number;
  /** Go to a moment, in seconds. */
  seek: (seconds: number) => void;
}

interface Props {
  src: string;
  title?: string;
  /** Where to open, in seconds. */
  startAt?: number;
  tracks?: PreviewTrack[];
  /**
   * A fresh address, for when this one has stopped working - expired,
   * or the request failed. Asked once; after that the screen says it
   * could not play rather than asking for ever.
   */
  onRefresh?: () => Promise<string | null | undefined>;
}

export const PreviewPlayer = forwardRef<PreviewPlayerHandle, Props>(
  ({ src, title, startAt = 0, tracks = [], onRefresh }, ref) => {
    const playerRef = useRef<MediaPlayerInstance>(null);
    const hlsRef = useRef<any>(null);
    const recoverDecoder = useRef(decoderRecovery());
    // The address actually playing, which a refresh replaces.
    const [playing, setPlaying] = useState(src);
    const [heldSrc, setHeldSrc] = useState(src);
    const [failed, setFailed] = useState(false);
    const refreshed = useRef(false);
    // Where to open: the start asked for, or where it was when it had
    // to be rebuilt.
    const openAt = useRef(startAt);
    const openedAt = useRef<string | null>(null);

    // A new address from outside is a new file to play: start over.
    // Set during render on purpose, which is what React asks for when
    // state has to follow a prop - an effect would run after the old
    // address had already been handed to the player again.
    if (src !== heldSrc) {
      setHeldSrc(src);
      setPlaying(src);
      setFailed(false);
      refreshed.current = false;
      openAt.current = startAt;
    }

    const isAdaptive = playing.includes('.m3u8');

    useImperativeHandle(ref, () => ({
      currentTime: () => playerRef.current?.currentTime ?? 0,
      seek: (seconds: number) => {
        if (playerRef.current) {
          playerRef.current.currentTime = seconds;
        }
      },
    }));

    const handleProvider = (provider: any) => {
      if (isHLSProvider(provider)) {
        settleHls(provider, {
          startAt: openAt.current,
          qualities: () => playerRef.current?.qualities,
          hlsRef,
        });
      }
    };

    // The ladder is told where to start before it loads anything (see
    // shared/settleHls). A plain file has no such setting, so it is
    // moved there once it can play - once per address, or every pause
    // and buffer after would send it back.
    const handleCanPlay = () => {
      if (openedAt.current === playing) {
        return;
      }
      openedAt.current = playing;
      if (!isAdaptive && openAt.current > 0 && playerRef.current) {
        playerRef.current.currentTime = openAt.current;
      }
    };

    // As the library player: only a fatal error has stopped the
    // stream, bar the decoder giving up, which nothing else repairs.
    const handleError = async (detail?: any) => {
      const decodeFailed = isAdaptive && isDecodeFailure(detail);
      if (decodeFailed && recoverDecoder.current(hlsRef.current)) {
        return;
      }
      if (isAdaptive && detail && detail.fatal !== true && !decodeFailed) {
        return;
      }
      if (refreshed.current || !onRefresh) {
        setFailed(true);
        return;
      }
      refreshed.current = true;
      openAt.current = playerRef.current?.currentTime ?? openAt.current;
      try {
        const fresh = await onRefresh();
        if (fresh) {
          setPlaying(fresh);
          return;
        }
      } catch {
        // Said below, the same as any other failure.
      }
      setFailed(true);
    };

    if (failed) {
      return (
        <div className="preview-player__failed">
          <p>Couldn&apos;t load this video.</p>
          <Button
            type="button"
            className="small"
            onClick={() => {
              refreshed.current = false;
              setFailed(false);
              setPlaying(`${playing}`);
            }}
          >
            Try again
          </Button>
        </div>
      );
    }

    return (
      <MediaPlayer
        className="preview-player"
        // A new address is a new player, so nothing of the old
        // stream's state carries over into it.
        key={playing}
        ref={playerRef}
        src={playing}
        title={title}
        onProviderChange={handleProvider}
        onCanPlay={handleCanPlay}
        onError={handleError}
        crossOrigin
        // Played in the page rather than handed to iOS's own
        // fullscreen player - see MediaModal2.
        playsInline
      >
        <MediaProvider>
          {tracks.map((track) => (
            <Track
              key={track.src}
              src={track.src}
              kind={track.kind}
              label={track.label}
              lang={track.lang}
              type="vtt"
              default={track.default}
            />
          ))}
        </MediaProvider>
        <DefaultVideoLayout icons={defaultLayoutIcons} />
      </MediaPlayer>
    );
  },
);

PreviewPlayer.displayName = 'PreviewPlayer';
